import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';

type AssistantContextFileType = 'schema' | 'draft';
type AssistantContextTheme = 'light' | 'dark';
type AssistantContextFilter = 'all' | AssistantContextFileType;
type ReviewFilter = 'all' | 'needs-review' | 'reviewed' | 'invalid';

interface AssistantContextFileSummary {
  path: string;
  name: string;
  directory: string;
  type: AssistantContextFileType;
  size: number;
  modified: string;
  modifiedUnix: number;
  jsonValid: boolean;
  manifestKey: string | null;
  publicTitle: string | null;
  needsHumanReview: boolean | null;
  routeCount: number;
}

interface AssistantContextListResponse {
  files: AssistantContextFileSummary[];
}

interface AssistantContextFileResponse {
  file: AssistantContextFileSummary;
  content: string;
  parsed: any;
  jsonValid: boolean;
  jsonError: string | null;
}

interface AssistantContextSaveResponse {
  message: string;
  file: AssistantContextFileSummary;
}

@Component({
  selector: 'app-assistant-context-manager',
  templateUrl: './assistant-context-manager.component.html',
  styleUrls: ['./assistant-context-manager.component.scss'],
})
export class AssistantContextManagerComponent implements OnInit {
  files: AssistantContextFileSummary[] = [];
  selectedFile: AssistantContextFileSummary | null = null;
  parsedManifest: any = null;

  editorText = '';
  originalText = '';
  searchText = '';
  typeFilter: AssistantContextFilter = 'all';
  reviewFilter: ReviewFilter = 'all';

  loadingList = false;
  loadingFile = false;
  saving = false;
  errorMessage = '';
  successMessage = '';
  jsonError = '';
  themeMode: AssistantContextTheme = 'light';

  private readonly apiUrl = '/api/assistant_context/assistant_context_files.php';
  private readonly themeStorageKey = 'assistantContextManagerTheme';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.themeMode = this.loadTheme();
    this.loadFiles();
  }

  get filteredFiles(): AssistantContextFileSummary[] {
    const query = this.searchText.trim().toLowerCase();

    return this.files.filter((file) => {
      const matchesType = this.typeFilter === 'all' || file.type === this.typeFilter;
      const matchesReview = this.matchesReviewFilter(file);
      const haystack = [
        file.path,
        file.manifestKey || '',
        file.publicTitle || '',
        file.type,
      ].join(' ').toLowerCase();

      return matchesType && matchesReview && (!query || haystack.includes(query));
    });
  }

  get draftCount(): number {
    return this.files.filter((file) => file.type === 'draft').length;
  }

  get schemaCount(): number {
    return this.files.filter((file) => file.type === 'schema').length;
  }

  get reviewCount(): number {
    return this.files.filter((file) => file.needsHumanReview === true).length;
  }

  get invalidCount(): number {
    return this.files.filter((file) => !file.jsonValid).length;
  }

  get hasChanges(): boolean {
    return this.editorText !== this.originalText;
  }

  get canSave(): boolean {
    return !!this.selectedFile && this.hasChanges && !this.jsonError && !this.saving && !this.loadingFile;
  }

  get editorLineCount(): number {
    if (!this.editorText) {
      return 0;
    }

    return this.editorText.split(/\r\n|\r|\n/).length;
  }

  get selectedRoutePaths(): string[] {
    const routes = this.parsedManifest?.routePaths;
    return Array.isArray(routes) ? routes : [];
  }

  get selectedAudience(): string[] {
    const audience = this.parsedManifest?.primaryAudience;
    return Array.isArray(audience) ? audience : [];
  }

  get selectedWorkflowCount(): number {
    return Array.isArray(this.parsedManifest?.commonWorkflows) ? this.parsedManifest.commonWorkflows.length : 0;
  }

  get selectedFieldCount(): number {
    return Array.isArray(this.parsedManifest?.importantFields) ? this.parsedManifest.importantFields.length : 0;
  }

  get selectedActionCount(): number {
    return Array.isArray(this.parsedManifest?.visibleActions) ? this.parsedManifest.visibleActions.length : 0;
  }

  loadFiles(selectPath = this.selectedFile?.path || ''): void {
    this.loadingList = true;
    this.errorMessage = '';

    this.http.get<AssistantContextListResponse>(this.apiUrl, { params: { action: 'list' } }).subscribe({
      next: (response) => {
        this.files = response.files || [];
        const nextSelection = this.files.find((file) => file.path === selectPath) || this.files[0] || null;

        if (nextSelection) {
          this.selectFile(nextSelection);
        } else {
          this.selectedFile = null;
          this.editorText = '';
          this.originalText = '';
          this.parsedManifest = null;
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.message || 'Unable to load assistant context files.';
      },
      complete: () => {
        this.loadingList = false;
      },
    });
  }

  selectFile(file: AssistantContextFileSummary): void {
    if (this.loadingFile || this.saving) {
      return;
    }

    this.selectedFile = file;
    this.loadingFile = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.jsonError = '';

    this.http.get<AssistantContextFileResponse>(this.apiUrl, {
      params: {
        action: 'file',
        path: file.path,
      },
    }).subscribe({
      next: (response) => {
        this.selectedFile = response.file;
        this.patchFileInList(response.file);
        this.editorText = response.content || '';
        this.originalText = this.editorText;
        this.parsedManifest = response.parsed;
        this.jsonError = response.jsonValid ? '' : (response.jsonError || 'JSON is invalid.');
      },
      error: (error) => {
        this.errorMessage = error?.error?.message || 'Unable to load the selected file.';
      },
      complete: () => {
        this.loadingFile = false;
      },
    });
  }

  onEditorTextChange(): void {
    this.successMessage = '';
    this.validateEditor();
  }

  formatJson(): void {
    this.errorMessage = '';
    this.successMessage = '';

    try {
      this.editorText = JSON.stringify(JSON.parse(this.editorText), null, 2) + '\n';
      this.validateEditor();
    } catch (error: any) {
      this.jsonError = error?.message || 'JSON is invalid.';
    }
  }

  revertFile(): void {
    if (!this.selectedFile) {
      return;
    }

    this.selectFile(this.selectedFile);
  }

  saveFile(): void {
    if (!this.selectedFile || !this.canSave) {
      return;
    }

    this.validateEditor();

    if (this.jsonError) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post<AssistantContextSaveResponse>(this.apiUrl, {
      path: this.selectedFile.path,
      content: this.editorText,
      expectedModified: this.selectedFile.modifiedUnix,
    }).subscribe({
      next: (response) => {
        this.selectedFile = response.file;
        this.patchFileInList(response.file);
        this.originalText = this.editorText.endsWith('\n') ? this.editorText : `${this.editorText}\n`;
        this.editorText = this.originalText;
        this.validateEditor();
        this.successMessage = response.message || 'File saved.';
      },
      error: (error) => {
        this.errorMessage = error?.error?.message || 'Unable to save file.';

        if (error?.error?.file) {
          this.patchFileInList(error.error.file);
        }
      },
      complete: () => {
        this.saving = false;
      },
    });
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Dark' : 'Light';
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-moon' : 'fa-solid fa-sun';
  }

  fileTypeLabel(file: AssistantContextFileSummary): string {
    return file.type === 'schema' ? 'Schema' : 'Draft';
  }

  reviewLabel(file: AssistantContextFileSummary): string {
    if (!file.jsonValid) {
      return 'Invalid';
    }

    if (file.needsHumanReview === true) {
      return 'Review';
    }

    if (file.needsHumanReview === false) {
      return 'Reviewed';
    }

    return 'Unmarked';
  }

  fileIcon(file: AssistantContextFileSummary): string {
    if (!file.jsonValid) {
      return 'fa-solid fa-triangle-exclamation';
    }

    return file.type === 'schema' ? 'fa-solid fa-code-branch' : 'fa-solid fa-file-lines';
  }

  formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes)) {
      return '-';
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  trackByPath(_: number, file: AssistantContextFileSummary): string {
    return file.path;
  }

  private validateEditor(): void {
    try {
      this.parsedManifest = this.editorText.trim() ? JSON.parse(this.editorText) : null;
      this.jsonError = '';
    } catch (error: any) {
      this.parsedManifest = null;
      this.jsonError = error?.message || 'JSON is invalid.';
    }
  }

  private patchFileInList(file: AssistantContextFileSummary): void {
    const index = this.files.findIndex((item) => item.path === file.path);

    if (index >= 0) {
      this.files = [
        ...this.files.slice(0, index),
        file,
        ...this.files.slice(index + 1),
      ];
    }
  }

  private matchesReviewFilter(file: AssistantContextFileSummary): boolean {
    switch (this.reviewFilter) {
      case 'needs-review':
        return file.needsHumanReview === true;
      case 'reviewed':
        return file.needsHumanReview === false;
      case 'invalid':
        return !file.jsonValid;
      default:
        return true;
    }
  }

  private loadTheme(): AssistantContextTheme {
    return localStorage.getItem(this.themeStorageKey) === 'dark' ? 'dark' : 'light';
  }
}
